import gql from 'graphql-tag';
import get from 'lodash/get';

import { getGraphQLClientWithToken } from 'utils/apiRequests';
import { EMPTY_VALUE } from 'Details/utils';
import { COLUMNS, CONDITIONS_SUFFIX_MAP } from '../tableMetadata';

const DEFAULT_SORT_COLUMN = COLUMNS.PO_BASE_SPEND;
const GQL_COLS = Object.keys(COLUMNS);

// inspired by https://www.ag-grid.com/graphql-server-side-operations/
export function createServerSideDatasource() {
	const convertSortModel = sortModel => {
		// default sorting to fix issues with total
		if (sortModel.length === 0) {
			sortModel = [{ colId: DEFAULT_SORT_COLUMN, sort: 'DESC' }];
		}

		const res = sortModel.reduce((acc, { colId, sort }) => {
			return { ...acc, [colId]: `${sort}`.toUpperCase() };
		}, {});

		return res;
	};

	const convertFilterModel = filterModel => {
		if (!filterModel || Object.keys(filterModel).length === 0) {
			return {
				ID_not_in: [-1],
			};
		}
		const convertCondition = (fieldName, filterData) => {
			let {
				filter,
				filterTo,
				type,
				filterType,
				values,
				dateFrom,
				dateTo,
			} = filterData;

			if (filter === EMPTY_VALUE) {
				filter = null;
			}

			const condition1Type = CONDITIONS_SUFFIX_MAP[type];
			let condition1Key = condition1Type
				? `${fieldName}_${condition1Type}`
				: fieldName;
			const isDateFilter = filterType === 'date';
			let value = !isDateFilter
				? type === 'string'
					? filter.toString()
					: filter
				: dateFrom;

			if (type === 'inRange') {
				const valueFrom = isDateFilter ? dateFrom : filter;
				const valueTo = isDateFilter ? dateTo : filterTo;

				return {
					[`${fieldName}_${CONDITIONS_SUFFIX_MAP.greaterThan}`]: valueFrom,
					[`${fieldName}_${CONDITIONS_SUFFIX_MAP.lessThan}`]: valueTo,
				};
			}

			// boolean set needs to be converted as equals filter
			if (
				filterType === 'set' &&
				(fieldName === COLUMNS.SOURCEABLE || fieldName === COLUMNS.IsTailSpend)
			) {
				return {
					[`${fieldName}`]: values[0] === 'true',
				};
			}

			if (filterType === 'set' && fieldName === COLUMNS.SPEND_TYPE) {
				if (values.length === 2 || values.length === 0) {
					return null;
				} else {
					return {
						[`${fieldName}`]: values[0],
					};
				}
			}

			if (filterType === 'set') {
				return {
					[`${fieldName}_in`]: values,
				};
			}

			return {
				[condition1Key]: value,
			};
		};

		const result = {
			AND: [
				...Object.keys(filterModel).map(fieldName => {
					const filterItem = filterModel[fieldName];

					if (filterItem.operator) {
						return {
							[filterItem.operator]: [
								convertCondition(fieldName, filterItem.condition1),
								convertCondition(fieldName, filterItem.condition2),
							].filter(Boolean),
						};
					} else {
						return convertCondition(fieldName, filterItem);
					}
				}),
			].filter(Boolean),
		};

		return result;
	};

	const query = (request, totalCount) => {
		const fields = GQL_COLS.join(' ');
		const gqlMethod = request.isUnpublished
			? 'unpublishedSpendCubeItems'
			: 'spendCubeItems';
		const sortingType = request.isUnpublished
			? 'GraphQLUnpublishedSpendCubeRowSort'
			: 'GraphQLSpendCubeRowSort';
		const filterType = request.isUnpublished
			? 'GraphQLUnpublishedSpendCubeRowFilter'
			: 'GraphQLSpendCubeRowFilter';

		const { startRow, endRow: fromItemsCount } = request;
		const pageItemsCount = fromItemsCount - startRow;
		// From {first} amount of items get last {last} items
		// totalCount equals 1 on first load (page)
		// Handle last page params correctly (e.g total = 212, page size = 200)
		// Last page params should be {first: 212, last: 12}
		const first =
			totalCount > 1
				? totalCount <= fromItemsCount
					? totalCount
					: fromItemsCount
				: fromItemsCount;
		const last =
			totalCount > 1
				? totalCount <= fromItemsCount
					? totalCount - startRow
					: pageItemsCount
				: pageItemsCount;

		return {
			query: gql`
			query (
				$first: PaginationAmount
				$last: PaginationAmount
				$sorting: ${sortingType}
				$filter: ${filterType}
				$dateFrom: DateTime
				$dateTo: DateTime
				$harmonized: Boolean
				$showOriginalAPTaxonomy: Boolean
				${!request.isUnpublished ? '$segmentName: String' : ''}
			)
			{
				${gqlMethod} (
					first: $first
					last: $last
					order_by: $sorting
					where: $filter
					dateFrom: $dateFrom
					dateTo: $dateTo
					harmonized: $harmonized
					showOriginalAPTaxonomy: $showOriginalAPTaxonomy
					${!request.isUnpublished ? 'segmentName: $segmentName' : ''}
				 )
				{
					nodes
					{
						${fields}
					},
					totalCount
				}
			}
			`,
			variables: {
				first,
				last,
				sorting: convertSortModel(request.sortModel),
				filter: convertFilterModel(request.filterModel),
				dateFrom: request.dateFrom,
				dateTo: request.dateTo,
				harmonized: request.harmonized === 'true',
				showOriginalAPTaxonomy: !!request.showOriginalAPTaxonomy,
				...(!request.isUnpublished ? { segmentName: request.segmentName } : {}),
			},
		};
	};

	let client = null;
	let filtersData = null;

	// clearing total sum on each load
	window.PO_BASE_SPEND_SUM = 0;

	return {
		setFiltersData: _filtersData => {
			filtersData = _filtersData;
		},

		getRows: async function(params) {
			const request = { ...params.request, ...(filtersData || {}) };
			const total = get(params, 'parentNode.childrenCache.displayIndexEnd', 1);
			const itemsProp = request.isUnpublished
				? 'unpublishedSpendCubeItems'
				: 'spendCubeItems';

			client = client || (await getGraphQLClientWithToken());
			const response = await client.query(query(request, total));

			if (response.data && response.data[itemsProp]) {
				// wrapping logging logic in try catch
				try {
					let sum = response.data[itemsProp].nodes.reduce(
						(sum, node) => node['PO_BASE_SPEND'] + sum,
						0
					);
					window.PO_BASE_SPEND_SUM = window.PO_BASE_SPEND_SUM
						? window.PO_BASE_SPEND_SUM + sum
						: sum;
					console.log(
						'Total calculation by PO_BASE_SPEND',
						Math.round(sum),
						Math.round(window.PO_BASE_SPEND_SUM)
					);
				} catch (_0) {}

				params.successCallback(
					response.data[itemsProp].nodes,
					response.data[itemsProp].totalCount
				);
			} else {
				params.failCallback();
			}
		},
	};
}
